import { actionCopyEnUs } from "./actionCopy";
import { listCopyEnUs } from "./listCopy";

export const studiesCopyEnUs = {
    studies: "Studies",
    description: "Create studies and invite clinicians with template-inferred access, labels, and billing.",
    unauthorizedMessage: "Studies are available to admins and managers only.",
    searchPlaceholder: "Search studies, labels, forms, or templates",
    searchAriaLabel: "Search studies",
    clearSearchAria: actionCopyEnUs.clearSearch,
    filterStudiesAria: "Filter studies",
    studyFilterDescription: "Filter studies by status and billing plan.",
    newStudy: "New study",
    noStudiesYet: "No studies yet. Create your first study to configure access and invites.",
    noStudiesMatchSearch: "No studies match your search or filters.",
    columnStudyName: "Study name",
    columnLabel: "Label",
    columnForms: "Forms",
    columnTemplates: "Templates",
    columnPlan: "Plan",
    columnExpiry: "Expiry",
    columnDiaries: "Diaries",
    columnClinicians: "Clinicians",
    columnStatus: listCopyEnUs.status,
    columnActions: listCopyEnUs.actions,
    published: listCopyEnUs.published,
    draft: listCopyEnUs.draft,
    open: actionCopyEnUs.open,
    invite: actionCopyEnUs.invite,
    edit: actionCopyEnUs.edit,
    openStudyAria: "Open study",
    inviteStudyAria: "Invite clinicians to this study",
    editStudyAria: "Edit study",
    openTrackingAria: "Open study progress charts",
    trackingDialogTitle: (studyName: string) => `Progress · ${studyName}`,
    emptyLabel: " - ",
    templateCount: (count: number) => `${count} template${count === 1 ? "" : "s"}`,
    newStudyTitle: "New study",
    newStudyDescription:
        "Choose templates to infer event, form, and schedule access, then customize grants, billing, and publish settings.",
    editStudyTitle: "Edit study",
    editStudyDescription:
        "Update templates, inferred access, custom grants, billing, and whether managers can see this study.",
    studySaved: "Study saved",
    failedToSaveStudy: "Failed to save study",
    saveStudy: "Save study",
    saveChanges: "Save changes",
    studyName: "Study name",
    studyNamePlaceholder: "e.g. Elite Tendon Knee 2026",
    studyDescription: "Description (optional)",
    studyDescriptionHint: "Clinicians see this when they open a study invite link.",
    studyDescriptionPlaceholder: "e.g. A multi-center registry following outcomes after knee tendon repair.",
    labelAccess: "Label access",
    selectLabels: "Select labels",
    addLabel: "Add label",
    labelName: "Label name",
    labelNamePlaceholder: "e.g. Elite Tendon Knee",
    formAccess: "Form access",
    selectForms: "Select forms",
    templates: "Templates",
    inferredAccessTitle: "Access from templates",
    inferredAccessDescription:
        "Questionnaires, schedules, and events required by the selected templates are granted automatically.",
    inferredAccessEmpty: "Select one or more templates to infer clinician access.",
    inferredEvents: "Events",
    inferredForms: "Questionnaires",
    inferredSchedules: "Schedules",
    inferredNone: "None",
    customiseAccessTitle: "Customize access",
    customiseAccessHideTitle: "Hide access options",
    customiseAccessDescription:
        "Full control over modes and private grants. Template-required questionnaires, schedules, and events stay selected.",
    loadingAccessOptions: "Loading access options…",
    billingPlan: "Billing plan",
    selectPlan: "Select plan",
    expiryDate: "Expiry date",
    pickDate: "DD/MM/YYYY",
    expiryDateMinMessage: "Expiry needs to be tomorrow or later",
    expiryDateMaxMessage: "Let's keep it within the next 100 years",
    expiryDateInvalidMessage: "That date doesn't look right",
    publishStudyForManagers: "Publish study",
    labelAccessSummary: "Label access",
    formAccessSummary: "Form access",
    templatesSummary: "Templates",
    billingPlanSummary: "Billing plan",
    expirySummary: "Expiry",
    statusSummary: "Status",
    loadingStudy: "Loading study…",
    studyNotFound: "Study not found",
    backToStudies: "Back to studies",
    studyInvite: "Study invite",
    inviteToStudy: (studyName: string) => `Invite to study ${studyName}`,
    inviteToStudyDescription:
        "Share a QR code or link so clinicians can sign up and join this study. Optionally pin an organization and specialty.",
    inviteStepReview: "Review",
    inviteStepEmail: "Email",
    inviteStepQr: "QR",
    inviteStepInvite: "Invite",
    generatingInviteLink: "Generating invite link…",
    failedToCreateInviteLink: "Failed to create invite link",
    studyInviteQrDescription: (studyName: string) =>
        `Share a QR code or link so clinicians can sign up and join ${studyName}. Organization and specialty come from the study defaults.`,
    joinStudyTitle: (studyName: string) => `Join ${studyName}`,
    optionalOrganisationSignupHint:
        "Optional. Leave empty for the clinician to create their own organization during onboarding.",
    clinicianWillChooseOrganisation: "The clinician will choose or create their organization at sign-up.",
    specialty: "Specialty",
    optionalSuffix: "(optional)",
    optionalSpecialtySignupHint: "Optional. When set, the specialty is applied to clinicians who join this study.",
    selectSpecialtyPlaceholder: "Select specialty",
    clearSpecialty: "Clear",
    clinicianInviteDefaultsTitle: "Clinician invite defaults",
    clinicianInviteDefaultsDescription:
        "Optionally pin an organization and specialty for clinicians who join this study via QR or invite link.",
    defaultOrganisation: "Organization",
    noOrganisationsAvailable: "No organizations available to select.",
    inviteDefaultsSummary: (summary: string) => `Invite defaults: ${summary}`,
    inviteDefaultsEmpty: "No organization or specialty pinned on this study. Clinicians set these during onboarding.",
    emailRequired: "Email address is required",
    validEmailRequired: "Enter a valid email address",
    studyInviteEmailSent: "Study invite email sent",
    failedToSendInvite: "Failed to send invite",
    trackingTitle: "Study tracking",
    trackingDescription:
        "Clinicians in this study, the diaries they have created, and anyone linked to it who is not in it yet.",
    trackingLabelsHint: "Labels",
    trackingLoading: "Loading tracking…",
    trackingError: "Could not load study tracking.",
    trackingNoLabels: "Add labels to this study to track clinicians and diaries.",
    trackingNoClinicians: "No clinicians have these labels yet.",
    trackingNoDiaries: "No diaries have been labeled for this study yet.",
    trackingTotalDiaries: "Diaries",
    trackingAvgDiariesPerClinician: "Avg diaries / clinician",
    trackingCliniciansWithDiaries: "Clinicians with diaries",
    trackingDiariesPerClinician: "Diaries per clinician",
    trackingCumulativeDiaries: "Cumulative diaries",
    trackingColumnMember: "Member",
    trackingColumnDiaries: "Diaries",
    trackingColumnRole: "Role",
    trackingColumnTemplates: "Templates",
    trackingColumnTrend: "Diaries over time",
    trackingColumnLastDiary: "Last diary",
    trackingLastDiaryNone: "None yet",
    trackingTemplatesFromStudy: (count: number) => `${count} from study`,
    trackingRoleLabels: {
        ADMIN: "Admin",
        MANAGER: "Manager",
        CLINICIAN: "Clinician",
        SECRETARY: "Secretary",
        VALIDATOR: "Validator",
    },
    trackingAddToStudy: "Add to study",
    trackingRemoveFromStudy: "Remove from study",
    trackingAddAllToStudy: "Add all to study",
    trackingCliniciansInStudy: "In study",
    trackingCliniciansInStudyHint: (total: number) => `of ${total} clinician${total === 1 ? "" : "s"}`,
    trackingStatusNotInStudy: "Not in study",
    trackingReasonHasLabels: "Has the study labels",
    trackingReasonHasDiaries: "Created study diaries",
    trackingNotInStudyCallout: (count: number) =>
        `${count} clinician${count === 1 ? " is" : "s are"} linked to this study but not in it yet. Adding them gives them its labels, questionnaires and templates.`,
    trackingRowActions: (name: string) => `Actions for ${name}`,
    trackingRemoveConfirmTitle: (name: string) => `Remove ${name} from the study?`,
    trackingRemoveConfirmDescription:
        "They lose the labels, questionnaires and templates that come with the study. Their diaries stay as they are, and anything shared with them directly is kept.",
    trackingCancel: "Cancel",
    trackingCliniciansShort: "clinicians",
    trackingNoLabelsShort: "No labels",
    trackingNoDiariesShort: "No diaries yet",
};
